import React, { useEffect, useState, useRef } from 'react';
import { T, FONTS, SHADOW } from '../constants/theme.js';

// ─── Toast ───────────────────────────────────────────────────────────────────
export function Toast({ message }) {
  if (!message) return null;
  const isErr = message.startsWith('❌') || message.toLowerCase().includes('error') || message.toLowerCase().includes('failed');
  return (
    <div style={{
      position:'fixed', bottom:24, right:24, zIndex:9999,
      background: isErr ? T.danger : T.success,
      color:T.white, padding:'12px 20px', borderRadius:12,
      fontSize:13, fontWeight:600, fontFamily:FONTS.body,
      boxShadow:SHADOW.lg, animation:'slideUp .25s ease',
      maxWidth:360, lineHeight:1.4,
    }}>
      {message}
    </div>
  );
}

// ─── Modal ───────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width = 520 }) {
  if (!open) return null;
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:1000,
      background:'rgba(13,27,42,.55)', display:'flex',
      alignItems:'center', justifyContent:'center', padding:20,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background:T.white, borderRadius:16, width:'100%', maxWidth:width,
        maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column',
        boxShadow:SHADOW.xl, animation:'scaleIn .2s ease',
      }}>
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'16px 22px', borderBottom:`1px solid ${T.border}`,
        }}>
          <h3 style={{ fontFamily:FONTS.display, fontSize:20, fontWeight:700, color:T.navy }}>{title}</h3>
          <button onClick={onClose} style={{
            background:'none', border:'none', fontSize:20, cursor:'pointer',
            color:T.muted, lineHeight:1, padding:4,
          }}>×</button>
        </div>
        <div style={{ overflowY:'auto', padding:'22px', flex:1 }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Card ────────────────────────────────────────────────────────────────────
export function Card({ children, style = {}, onClick }) {
  return (
    <div onClick={onClick} style={{
      background:T.white, border:`1px solid ${T.border}`,
      borderRadius:14, padding:'20px 22px',
      boxShadow:SHADOW.sm, cursor:onClick ? 'pointer' : 'default',
      transition:'transform .18s, box-shadow .18s',
      ...style,
    }}
    onMouseEnter={e => onClick && (e.currentTarget.style.boxShadow = SHADOW.md)}
    onMouseLeave={e => onClick && (e.currentTarget.style.boxShadow = SHADOW.sm)}>
      {children}
    </div>
  );
}

// ─── Btn ─────────────────────────────────────────────────────────────────────
export function Btn({ children, onClick, variant = 'primary', size = 'md', disabled, style = {}, type = 'button' }) {
  const variants = {
    primary:  { background:T.teal,    color:T.white, border:`1px solid ${T.teal}` },
    secondary:{ background:T.surface, color:T.slate, border:`1px solid ${T.border}` },
    danger:   { background:T.danger,  color:T.white, border:`1px solid ${T.danger}` },
    ghost:    { background:'none',    color:T.teal,  border:`1px solid ${T.teal}` },
    navy:     { background:T.navy,    color:T.white, border:`1px solid ${T.navy}` },
    gold:     { background:T.gold,    color:T.navy,  border:`1px solid ${T.gold}` },
  };
  const sizes = {
    sm: { padding:'6px 14px', fontSize:11, borderRadius:8 },
    md: { padding:'9px 20px', fontSize:13, borderRadius:10 },
    lg: { padding:'12px 28px', fontSize:14, borderRadius:12 },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      ...variants[variant], ...sizes[size],
      fontFamily:FONTS.body, fontWeight:700, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? .6 : 1, transition:'all .18s', whiteSpace:'nowrap', ...style,
    }}>
      {children}
    </button>
  );
}

// ─── Field / Input ────────────────────────────────────────────────────────────
export function Field({ label, required, children, hint }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{
        display:'block', fontSize:10, fontWeight:700, color:T.muted,
        letterSpacing:.8, textTransform:'uppercase', marginBottom:5,
      }}>
        {label}{required && <span style={{ color:T.danger }}> *</span>}
      </label>
      {children}
      {hint && <p style={{ fontSize:10, color:T.muted, marginTop:4 }}>{hint}</p>}
    </div>
  );
}

export function Input({ value, onChange, type = 'text', placeholder, required, min, max, maxLength, disabled, rows, autoComplete, style = {} }) {
  const base = {
    width:'100%', border:`1px solid ${T.border}`, borderRadius:10,
    padding:'9px 12px', fontSize:13, fontFamily:FONTS.body, color:T.navy,
    outline:'none', boxSizing:'border-box', background: disabled ? T.surface : T.white,
    resize:'vertical', ...style,
  };
  const focus = e => e.target.style.borderColor = T.teal;
  const blur  = e => e.target.style.borderColor = T.border;
  if (rows) return <textarea rows={rows} value={value} onChange={e => onChange(e.target.value)}
    placeholder={placeholder} disabled={disabled} maxLength={maxLength}
    style={base} onFocus={focus} onBlur={blur} />;
  return <input type={type} value={value} onChange={e => onChange(e.target.value)}
    placeholder={placeholder} required={required} min={min} max={max} maxLength={maxLength}
    disabled={disabled} autoComplete={autoComplete}
    style={base} onFocus={focus} onBlur={blur} />;
}

export function Select({ value, onChange, children, disabled, style = {} }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled} style={{
      width:'100%', border:`1px solid ${T.border}`, borderRadius:10,
      padding:'9px 12px', fontSize:13, fontFamily:FONTS.body, color:T.navy,
      outline:'none', boxSizing:'border-box', background:T.white, cursor:'pointer',
      ...style,
    }}>
      {children}
    </select>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────────────
export function Badge({ label, color = T.teal, bg }) {
  return (
    <span style={{
      display:'inline-block', padding:'2px 10px', borderRadius:20,
      fontSize:10, fontWeight:700, letterSpacing:.4, textTransform:'uppercase',
      color: color, background: bg || color + '18', border:`1px solid ${color}30`,
    }}>
      {label}
    </span>
  );
}

// ─── Status Badge (reservation statuses) ──────────────────────────────────────
const STATUS_MAP = {
  confirmed:   { label:'Confirmed',   color:T.teal },
  tentative:   { label:'Tentative',   color:T.warning },
  inquiry:     { label:'Inquiry',     color:T.muted },
  checked_in:  { label:'In-House',    color:T.success },
  checked_out: { label:'Checked Out', color:T.slate },
  cancelled:   { label:'Cancelled',   color:T.danger },
  no_show:     { label:'No-Show',     color:T.danger },
  waitlisted:  { label:'Waitlisted',  color:T.purple },
};

export function ResBadge({ status }) {
  const s = STATUS_MAP[status] || { label: status, color: T.muted };
  return <Badge label={s.label} color={s.color} />;
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
export function Spinner({ size = 36, color = T.teal }) {
  return (
    <div style={{
      width:size, height:size, border:`3px solid ${T.border}`,
      borderTopColor:color, borderRadius:'50%',
      animation:'spin 1s linear infinite', flexShrink:0,
    }} />
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
export function Empty({ icon = '📭', title, subtitle, action }) {
  return (
    <div style={{ textAlign:'center', padding:'60px 20px', color:T.muted }}>
      <div style={{ fontSize:48, marginBottom:12 }}>{icon}</div>
      <p style={{ fontSize:16, fontWeight:700, color:T.slate, marginBottom:4 }}>{title}</p>
      {subtitle && <p style={{ fontSize:13, color:T.muted, marginBottom:16 }}>{subtitle}</p>}
      {action}
    </div>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────
export function SectionHeader({ title, subtitle, right }) {
  return (
    <div className="r-section-header">
      <div>
        <h1 style={{ fontFamily:FONTS.display, fontSize:'clamp(20px,4vw,28px)', fontWeight:700, color:T.navy, marginBottom:2 }}>{title}</h1>
        {subtitle && <p style={{ fontSize:13, color:T.muted }}>{subtitle}</p>}
      </div>
      {right && <div className="r-section-header-actions">{right}</div>}
    </div>
  );
}

// ─── KPI card ─────────────────────────────────────────────────────────────────
export function KPICard({ icon, label, value, sub, color = T.teal, onClick }) {
  return (
    <div onClick={onClick} style={{
      background:T.white, border:`1px solid ${T.border}`, borderRadius:14,
      padding:'18px 20px', position:'relative', overflow:'hidden',
      cursor:onClick ? 'pointer' : 'default', boxShadow:SHADOW.sm,
      transition:'transform .2s, box-shadow .2s',
    }}
    onMouseEnter={e => onClick && (e.currentTarget.style.transform = 'translateY(-2px)')}
    onMouseLeave={e => onClick && (e.currentTarget.style.transform = 'translateY(0)')}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${color},${color}44)` }} />
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <p style={{ fontSize:9, fontWeight:700, color:T.muted, letterSpacing:.8, textTransform:'uppercase', marginBottom:6 }}>{label}</p>
          <p style={{ fontFamily:FONTS.display, fontSize:32, fontWeight:700, color:T.navy, lineHeight:1, marginBottom:2 }}>{value}</p>
          {sub && <p style={{ fontSize:11, color:T.muted }}>{sub}</p>}
        </div>
        <div style={{ fontSize:26, background:`${color}15`, width:48, height:48, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center' }}>{icon}</div>
      </div>
    </div>
  );
}

// ─── Grid ─────────────────────────────────────────────────────────────────────
export function Grid({ cols = 4, gap = 16, children, className = '' }) {
  // Use CSS class r-grid-4 or r-grid-2 for responsive behaviour
  const cls = cols === 4 ? 'r-grid-4' : cols === 2 ? 'r-grid-2' : '';
  if (cls) {
    return (
      <div className={`${cls} ${className}`} style={{ gap, marginBottom:20 }}>
        {children}
      </div>
    );
  }
  return (
    <div style={{ display:'grid', gridTemplateColumns:`repeat(${cols}, 1fr)`, gap, marginBottom:20 }}>
      {children}
    </div>
  );
}

// ─── Table ────────────────────────────────────────────────────────────────────
export function Table({ headers, children, style = {} }) {
  return (
    <div style={{ overflowX:'auto', ...style }}>
      <table style={{ width:'100%', borderCollapse:'collapse', tableLayout:'auto' }}>
        <thead>
          <tr style={{ background:T.surface }}>
            {headers.map((h, i) => (
              <th key={i} style={{ padding:'10px 14px', textAlign:'left', fontSize:9, fontWeight:700, color:T.muted, letterSpacing:.6, textTransform:'uppercase', whiteSpace:'nowrap', borderBottom:`1px solid ${T.border}` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function TR({ children, onClick, highlight }) {
  return (
    <tr onClick={onClick} style={{
      borderBottom:`1px solid ${T.border}`,
      background: highlight ? `${T.tealPale}` : 'transparent',
      cursor:onClick ? 'pointer' : 'default',
      transition:'background .15s',
    }}
    onMouseEnter={e => onClick && (e.currentTarget.style.background = T.surface)}
    onMouseLeave={e => onClick && (e.currentTarget.style.background = highlight ? T.tealPale : 'transparent')}>
      {children}
    </tr>
  );
}

export function TD({ children, style = {} }) {
  return <td style={{ padding:'11px 14px', fontSize:13, color:T.navy, verticalAlign:'middle', ...style }}>{children}</td>;
}

// ─── Tab bar ──────────────────────────────────────────────────────────────────
export function Tabs({ tabs, active, onSelect }) {
  return (
    <div style={{ display:'flex', gap:4, borderBottom:`1px solid ${T.border}`, marginBottom:20 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onSelect(t.id)} style={{
          background:'none', border:'none', padding:'8px 16px', fontSize:12,
          fontWeight: active === t.id ? 700 : 500, cursor:'pointer', fontFamily:FONTS.body,
          color: active === t.id ? T.navy : T.muted,
          borderBottom:`2px solid ${active === t.id ? T.teal : 'transparent'}`,
          transition:'all .15s', whiteSpace:'nowrap',
        }}>
          {t.icon && <span style={{ marginRight:5 }}>{t.icon}</span>}
          {t.label}
          {t.count != null && (
            <span style={{ marginLeft:5, background:`${active===t.id?T.teal:T.muted}20`, color:active===t.id?T.teal:T.muted, borderRadius:10, padding:'1px 7px', fontSize:10, fontWeight:700 }}>
              {t.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ─── Currency formatter ───────────────────────────────────────────────────────
export function fmtCurrency(amount, currency = 'USD') {
  const n = parseFloat(amount || 0);
  return new Intl.NumberFormat('en-US', { style:'currency', currency, minimumFractionDigits:0, maximumFractionDigits:0 }).format(n);
}

/**
 * Parse a date value without shifting the day due to UTC vs local-time mismatch.
 * `new Date("2026-05-25")` is UTC midnight which in e.g. UTC+2 becomes May 24 locally.
 * We force noon-UTC so the date stays on the intended calendar day in any timezone.
 */
function parseDateSafe(d) {
  if (!d) return null;
  const s = String(d);
  // If it's a plain YYYY-MM-DD (no time component), append noon UTC
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s + 'T12:00:00Z');
  return new Date(s);
}

export function fmtDate(d) {
  if (!d) return '—';
  const dt = parseDateSafe(d);
  if (!dt || isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
}

export function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' });
}

export function initials(name) {
  if (!name) return '?';
  return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
}

export function Avatar({ name, size = 36, color = T.gold }) {
  return (
    <div style={{
      width:size, height:size, borderRadius:'50%',
      background:`${color}22`, display:'flex', alignItems:'center',
      justifyContent:'center', fontSize:size * .38, fontWeight:700,
      color, flexShrink:0, letterSpacing:-.5,
    }}>
      {initials(name)}
    </div>
  );
}

// ─── Search input ─────────────────────────────────────────────────────────────
export function SearchInput({ value, onChange, placeholder = 'Search…' }) {
  return (
    <div style={{ position:'relative' }}>
      <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', fontSize:14 }}>🔍</span>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{
        paddingLeft:36, paddingRight:12, paddingTop:9, paddingBottom:9,
        border:`1px solid ${T.border}`, borderRadius:10, fontSize:13,
        fontFamily:FONTS.body, color:T.navy, outline:'none',
        width:'100%', boxSizing:'border-box',
      }} />
    </div>
  );
}

// ─── Paginator ────────────────────────────────────────────────────────────────
export function Paginator({ page, total, size, onChange }) {
  const pages   = Math.ceil(total / size) || 1;
  const fromRow = total === 0 ? 0 : page * size + 1;
  const toRow   = Math.min((page + 1) * size, total);
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
      <span style={{ fontSize:11, color:T.muted }}>
        {total === 0 ? 'No records' : `Showing ${fromRow}–${toRow} of ${total}`}
      </span>
      {pages > 1 && (
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <Btn variant="secondary" size="sm" disabled={page === 0} onClick={() => onChange(page - 1)}>‹ Prev</Btn>
          <span style={{ fontSize:12, color:T.muted, padding:'0 8px' }}>Page {page + 1} of {pages}</span>
          <Btn variant="secondary" size="sm" disabled={page >= pages - 1} onClick={() => onChange(page + 1)}>Next ›</Btn>
        </div>
      )}
    </div>
  );
}

// ─── OfflineBadge ─────────────────────────────────────────────────────────────
export function OfflineBadge({ item, style = {} }) {
  if (!item?._offline && !item?._pending) return null;
  return (
    <span title="Saved locally — syncs automatically when server is available"
      style={{
        display:'inline-flex', alignItems:'center', gap:3,
        background:`${T.gold}22`, border:`1px solid ${T.gold}55`,
        borderRadius:99, padding:'1px 7px',
        fontSize:9, fontWeight:700, color:T.gold,
        fontFamily:FONTS.body, whiteSpace:'nowrap', letterSpacing:.3, ...style,
      }}>
      ⏳ offline
    </span>
  );
}

export function OfflineBanner({ isOffline, pendingCount = 0 }) {
  if (!isOffline) return null;
  return (
    <div style={{
      background:`${T.gold}15`, border:`1px solid ${T.gold}40`,
      borderRadius:10, padding:'9px 14px', marginBottom:14,
      display:'flex', alignItems:'center', gap:10,
      fontSize:12, color:T.navy, fontFamily:FONTS.body,
    }}>
      <span style={{ fontSize:16 }}>📵</span>
      <div style={{ flex:1 }}>
        <strong>Working offline</strong> — data loaded from local cache.
        {pendingCount > 0 && (
          <span style={{ marginLeft:6, color:T.gold, fontWeight:700 }}>
            {pendingCount} change{pendingCount > 1 ? 's' : ''} queued to sync.
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Subscribe to offline mutation events so a view reloads when its data changes.
 *
 * Two usage patterns:
 *   1. Callback style (preferred):
 *        useOfflineInvalidation('reservations', loadReservations);
 *        // loadReservations() is called automatically on relevant events
 *
 *   2. Tick style (legacy):
 *        const tick = useOfflineInvalidation('reservations');
 *        useEffect(() => { loadReservations(); }, [tick]);
 */
export function useOfflineInvalidation(pathPrefix, onInvalidate) {
  const [tick, setTick] = useState(0);
  const cbRef = useRef(onInvalidate);
  useEffect(() => { cbRef.current = onInvalidate; }, [onInvalidate]);

  useEffect(() => {
    const bump = e => {
      let path = '';
      let invalidatePrefix = '';
      if (typeof e.detail === 'string') {
        invalidatePrefix = e.detail;
      } else if (e.detail && typeof e.detail === 'object') {
        path = e.detail.path || '';
        invalidatePrefix = e.detail.invalidatePrefix || '';
      }
      const haystack = `${path} ${invalidatePrefix}`;
      if (haystack.includes(pathPrefix) || path.includes(pathPrefix)) {
        setTick(t => t + 1);
        cbRef.current?.();
      }
    };
    // Also re-run on sync:done so cached data refreshes after reconnect
    const onDone = () => { setTick(t => t + 1); cbRef.current?.(); };
    // Re-run after the offline bridge finishes hydrating IndexedDB from Dexie
    // so views that loaded before the bridge completed can pick up the data.
    const onBridge = () => { setTick(t => t + 1); cbRef.current?.(); };

    window.addEventListener('hotelmis:data:mutated',    bump);
    window.addEventListener('hotelmis:sync:invalidate', bump);
    window.addEventListener('hotelmis:sync:done',       onDone);
    window.addEventListener('hotelmis:bridge:hydrated', onBridge);
    return () => {
      window.removeEventListener('hotelmis:data:mutated',    bump);
      window.removeEventListener('hotelmis:sync:invalidate', bump);
      window.removeEventListener('hotelmis:sync:done',       onDone);
      window.removeEventListener('hotelmis:bridge:hydrated', onBridge);
    };
  }, [pathPrefix]);
  return tick;
}